import { Job, SupabaseJob } from '@applicator/shared'
import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { TableColumn } from '../Table'

interface TableHandlersParams {
  jobs: Job[]
  sortedFilteredJobs: Job[]
  selectedRows: Set<number>
  supabaseJobs: SupabaseJob[]
  searchQueryId: string | null | undefined
  sortColumn: string | null
  sortDirection: 'asc' | 'desc'
  tableColumns: TableColumn<Job>[]
  handleSort: (column: string) => void
  allJobsLoaded: boolean
  isLoadingMore: boolean
  setSelectedRows: React.Dispatch<React.SetStateAction<Set<number>>>
  setDisplayedJobsCount: React.Dispatch<React.SetStateAction<number>>
  setAllJobsLoaded: React.Dispatch<React.SetStateAction<boolean>>
  setIsLoadingMore: React.Dispatch<React.SetStateAction<boolean>>
}

export const useTableHandlers = ({
  jobs,
  sortedFilteredJobs,
  selectedRows,
  supabaseJobs,
  searchQueryId,
  _sortColumn,
  _sortDirection,
  tableColumns,
  handleSort,
  allJobsLoaded,
  isLoadingMore,
  setSelectedRows,
  setDisplayedJobsCount,
  setAllJobsLoaded,
  setIsLoadingMore,
}: TableHandlersParams) => {
  const navigate = useNavigate()

  // Map selectedRows from job indices to sortedFilteredJobs indices
  const tableSelectedRows = useMemo(() => {
    const selected = new Set<number>()
    sortedFilteredJobs.forEach((job, index) => {
      const originalIndex = job.id ? jobs.findIndex((j) => j.id === job.id) : -1
      const safeOriginalIndex = originalIndex >= 0 ? originalIndex : index
      if (selectedRows.has(safeOriginalIndex)) {
        selected.add(index)
      }
    })
    return selected
  }, [selectedRows, sortedFilteredJobs, jobs])

  // Handle row selection in Table component
  const handleTableRowSelect = useCallback((selectedIndices: Set<number>) => {
    // Map back from sortedFilteredJobs indices to jobs indices
    const newSelected = new Set<number>()
    selectedIndices.forEach((tableIndex) => {
      const job = sortedFilteredJobs[tableIndex]
      if (job) {
        const originalIndex = job.id ? jobs.findIndex((j) => j.id === job.id) : -1
        const safeOriginalIndex = originalIndex >= 0 ? originalIndex : tableIndex
        newSelected.add(safeOriginalIndex)
      }
    })
    setSelectedRows(newSelected)
  }, [sortedFilteredJobs, jobs, setSelectedRows])

  // Handle sorting in Table component
  const handleTableSort = useCallback((columnKey: string) => {
    const column = tableColumns.find(col => col.key === columnKey)
    if (!column || !column.sortable) return
    
    // Map column keys to old column names for compatibility
    const columnNameMap: Record<string, string> = {
      'position': 'Job Position',
      'company': 'Company',
      'salary': 'Salary',
      'location': 'Location',
      'status': 'Status',
      'dateSaved': 'Date Saved',
      'dateApplied': 'Date Applied',
      'followUp': 'Follow up',
      'deadline': 'Deadline',
      'excitement': 'Excitement',
      'score': 'Score',
      'verdict': 'Verdict',
    }
    
    const oldColumnName = columnNameMap[columnKey] || columnKey
    handleSort(oldColumnName)
  }, [tableColumns, handleSort])

  // Handle row click
  const handleTableRowClick = useCallback((job: Job, index: number) => {
    const jobId = job.id || index.toString()
    const url = searchQueryId 
      ? `/job/${jobId}?searchQueryId=${searchQueryId}`
      : `/job/${jobId}`
    navigate(url)
  }, [searchQueryId, navigate])

  // Row className based on relevance
  const getRowClassName = useCallback((job: Job, index: number) => {
    const jobId = job.id || index.toString()
    const supabaseJob = supabaseJobs.find(j => j.id === jobId)
    const isRelevant = supabaseJob?.relevance_verdict === 'Fit'
    const isNotRelevant = supabaseJob?.relevance_verdict === 'No Fit'
    
    return `border-b border-border-default transition-colors ${
      isRelevant ? 'bg-brand-primary-subtle/50 hover:bg-brand-primary-subtle' :
      isNotRelevant ? 'bg-danger-subtle/50 hover:bg-danger-subtle' :
      'hover:bg-surface-subtle'
    }`
  }, [supabaseJobs])

  // Lazy loading handler
  const handleLoadMore = useCallback(async () => {
    if (allJobsLoaded || isLoadingMore) return
    
    setIsLoadingMore(true)
    setDisplayedJobsCount((prev) => {
      const newCount = prev + 20
      const currentFilteredCount = sortedFilteredJobs.length
      if (newCount >= currentFilteredCount) {
        setAllJobsLoaded(true)
        return currentFilteredCount
      }
      return newCount
    })
    
    setTimeout(() => {
      setIsLoadingMore(false)
    }, 300)
  }, [allJobsLoaded, isLoadingMore, sortedFilteredJobs.length, setIsLoadingMore, setDisplayedJobsCount, setAllJobsLoaded])

  return {
    tableSelectedRows,
    handleTableRowSelect,
    handleTableSort,
    handleTableRowClick,
    getRowClassName,
    handleLoadMore,
  }
}
